(() => {
  const init = (root = document) => {
    root.querySelectorAll('[data-lpv2]').forEach((page) => {
      if (page.dataset.ready === 'true') return;
      page.dataset.ready = 'true';

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!reduced && page.classList.contains('lpv2--animate')) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.14, rootMargin: '0px 0px -5% 0px' });

        page.querySelectorAll('.lpv2-reveal').forEach((el) => observer.observe(el));
      } else {
        page.querySelectorAll('.lpv2-reveal').forEach((el) => el.classList.add('is-visible'));
      }

      const library = page.querySelector('[data-lpv2-library]');
      if (library) {
        const tabs = [...library.querySelectorAll('[data-lpv2-category]')];
        const clips = [...library.querySelectorAll('.lpv2-clip')];
        const search = library.querySelector('[data-lpv2-search]');
        const title = library.querySelector('[data-lpv2-category-title]');
        const empty = library.querySelector('[data-lpv2-empty]');
        const video = library.querySelector('[data-lpv2-video]');
        const placeholder = library.querySelector('[data-lpv2-player-placeholder]');
        const playerTitle = library.querySelector('[data-lpv2-player-title]');
        const playerDuration = library.querySelector('[data-lpv2-player-duration]');
        const playerResolution = library.querySelector('[data-lpv2-player-resolution]');
        const playerFormat = library.querySelector('[data-lpv2-player-format]');
        const playerDescription = library.querySelector('[data-lpv2-player-description]');
        let active = tabs[0]?.dataset.lpv2Category || '';

        const getVisibleClips = () => clips.filter((clip) => !clip.hidden);

        const updatePlayer = (clip, attemptPlay = true) => {
          if (!clip || !video) return;

          const src = clip.dataset.video || '';
          const poster = clip.dataset.poster || '';

          if (playerTitle) playerTitle.textContent = clip.dataset.title || 'Preview clip';
          if (playerDuration) playerDuration.textContent = clip.dataset.duration || '—';
          if (playerResolution) playerResolution.textContent = clip.dataset.resolution || '—';
          if (playerFormat) playerFormat.textContent = clip.dataset.format || '—';
          if (playerDescription) playerDescription.textContent = clip.dataset.description || 'Preview the selected clip before getting full access.';

          clips.forEach((item) => item.classList.toggle('is-selected', item === clip));

          if (poster) video.setAttribute('poster', poster);
          else video.removeAttribute('poster');

          if (!src) {
            video.pause();
            video.removeAttribute('src');
            video.load();
            if (placeholder) placeholder.hidden = Boolean(poster);
            return;
          }

          if (video.getAttribute('src') !== src) {
            video.pause();
            video.setAttribute('src', src);
            video.load();
          }

          if (placeholder) placeholder.hidden = true;
          video.style.display = 'block';

          if (clip.dataset.autoplay !== 'false' && attemptPlay) {
            video.muted = true;
            video.autoplay = true;
            video.loop = true;
            video.playsInline = true;
            video.controls = false;

            const tryPlay = () => {
              const promise = video.play();
              if (promise && typeof promise.catch === 'function') {
                promise.catch(() => {});
              }
            };

            if (video.readyState >= 2) {
              tryPlay();
            } else {
              video.addEventListener('canplay', tryPlay, { once: true });
            }
          }
        };

        const applyFilter = (selectFirst = true) => {
          const query = (search?.value || '').trim().toLowerCase();
          let visibleCount = 0;

          clips.forEach((clip) => {
            const categoryMatch = !active || clip.dataset.category === active;
            const text = `${clip.dataset.title || ''} ${clip.dataset.categoryLabel || ''}`.toLowerCase();
            const searchMatch = !query || text.includes(query);
            const visible = categoryMatch && searchMatch;

            clip.hidden = !visible;
            if (visible) visibleCount += 1;
          });

          if (empty) empty.hidden = visibleCount > 0;

          if (selectFirst) {
            const first = getVisibleClips()[0];
            if (first) updatePlayer(first, true);
            else if (video) {
              video.pause();
              video.removeAttribute('src');
              video.load();
              if (placeholder) placeholder.hidden = true;
            }
          }
        };

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            tabs.forEach((item) => {
              item.classList.remove('is-active');
              item.setAttribute('aria-selected', 'false');
            });

            tab.classList.add('is-active');
            tab.setAttribute('aria-selected', 'true');
            active = tab.dataset.lpv2Category || '';

            if (title) {
              title.textContent = tab.querySelector('span')?.textContent || tab.textContent.trim();
            }

            applyFilter(true);
          });
        });

        search?.addEventListener('input', () => applyFilter(true));

        clips.forEach((clip) => {
          clip.addEventListener('click', () => updatePlayer(clip, true));
        });

        video?.addEventListener('click', () => {
          if (video.paused) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });

        video?.addEventListener('loadedmetadata', () => {
          if (Number.isFinite(video.duration) && playerDuration && playerDuration.textContent === '—') {
            const total = Math.round(video.duration);
            const minutes = Math.floor(total / 60);
            const seconds = String(total % 60).padStart(2, '0');
            playerDuration.textContent = `${minutes}:${seconds}`;
          }
        });

        applyFilter(true);
      }

      const useSection = page.querySelector('[data-lpv2-tabs]');
      if (useSection) {
        const tabs = [...useSection.querySelectorAll('[data-lpv2-use-tab]')];
        const panels = [...useSection.querySelectorAll('[data-lpv2-use-panel]')];

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            tabs.forEach((item) => item.classList.remove('is-active'));
            panels.forEach((panel) => panel.classList.remove('is-active'));

            tab.classList.add('is-active');
            useSection.querySelector(`[data-lpv2-use-panel="${CSS.escape(tab.dataset.lpv2UseTab)}"]`)?.classList.add('is-active');
          });
        });
      }

      page.querySelectorAll('.lpv2-faq-item').forEach((item) => {
        const button = item.querySelector('button');
        button?.addEventListener('click', () => {
          const isOpen = item.classList.toggle('is-open');
          button.setAttribute('aria-expanded', String(isOpen));
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
